import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  clientes as apiClientes,
  ventas as apiVentas,
  abonos as apiAbonos,
  nombreFormaPago,
} from "../lib/api";
import { formatCurrency, formatDateTime, getTodayLocal } from "../lib/formato";
import PaginationControls from "../componentes/PaginationControls";
import ConfirmDeleteModal from "../componentes/ConfirmDeleteModal";
import Aviso from "../componentes/Aviso";
import AddTransactionModal from "../componentes/transacciones/AddTransactionModal";
import VentaDetailModal from "../componentes/transacciones/VentaDetailModal";
import AbonoDetailModal from "../componentes/transacciones/AbonoDetailModal";
import RegistrarMovimientoModal from "../componentes/movimientos/RegistrarMovimientoModal";
import ClientModal from "../componentes/clientes/ClientModal";
import BotonCompartir from "../componentes/BotonCompartir";
import { estadoDeCuenta } from "../lib/comprobante";
import {
  BackIcon,
  PhoneIcon,
  PinIcon,
  EditIcon,
  TrashIcon,
  EyeIcon,
  EyeOffIcon,
  PlusIcon,
} from "../componentes/Iconos";

const POR_PAGINA = 5;

// Tarjeta de resumen. Los totales van tapados por defecto: la pantalla
// del negocio queda de frente al mostrador. El saldo pendiente sí se
// muestra, porque es lo que el cliente que está ahí viene a preguntar.
const TarjetaResumen = ({ clase, titulo, monto, ocultable = false, destacada = false }) => {
  const [visible, setVisible] = useState(!ocultable);

  return (
    <div className={`tarjeta-total ${clase} ${destacada ? "destacada" : ""}`}>
      <div className="tarjeta-total-encabezado">
        <h4>{titulo}</h4>
        {ocultable && (
          <button
            type="button"
            className="ojo-total"
            onClick={() => setVisible((v) => !v)}
            aria-pressed={visible}
            title={visible ? `Ocultar ${titulo.toLowerCase()}` : `Mostrar ${titulo.toLowerCase()}`}
          >
            {visible ? <EyeIcon /> : <EyeOffIcon />}
          </button>
        )}
      </div>
      <p className="tarjeta-total-monto">
        {visible ? formatCurrency(monto) : <span className="masked-value">••••••</span>}
      </p>
    </div>
  );
};

// Una línea del historial, con la franja de color del tipo de movimiento.
const FilaHistorial = ({ tipo, titulo, subtitulo, monto, onVer, onEditar, onEliminar }) => (
  <li className="fila-historial" onClick={onVer}>
    <span className={`franja ${tipo}`} />
    <div className="fila-historial-texto">
      <span className="fila-historial-titulo">{titulo}</span>
      {subtitulo && <span className="fila-historial-sub">{subtitulo}</span>}
    </div>
    <span className={`fila-historial-monto monto-${tipo}`}>{formatCurrency(monto)}</span>
    <div className="fila-historial-acciones">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEditar();
        }}
        className="card-action-btn edit"
        title="Editar"
      >
        <EditIcon />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEliminar();
        }}
        className="card-action-btn delete"
        title="Eliminar"
      >
        <TrashIcon />
      </button>
    </div>
  </li>
);

const ClienteDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState(null);
  const [cargandoCliente, setCargandoCliente] = useState(true);
  const [errorCliente, setErrorCliente] = useState(null);

  const [ventas, setVentas] = useState({ items: [], totalPages: 0 });
  const [abonos, setAbonos] = useState({ items: [], totalPages: 0 });
  const [paginaVentas, setPaginaVentas] = useState(1);
  const [paginaAbonos, setPaginaAbonos] = useState(1);
  const [saldos, setSaldos] = useState({
    total_todas_ventas: 0,
    total_abonos: 0,
    saldo_pendiente: 0,
  });

  const [registrando, setRegistrando] = useState(false);
  const [editandoCliente, setEditandoCliente] = useState(false);
  const [transaccionEnEdicion, setTransaccionEnEdicion] = useState(null);
  const [confirmar, setConfirmar] = useState({ abierto: false, tipo: null, id: null });
  const [ventaVista, setVentaVista] = useState(null);
  const [abonoVisto, setAbonoVisto] = useState(null);
  const [aviso, setAviso] = useState(null);

  // El cliente se pide por id: así la pantalla funciona aunque se entre
  // directo por la URL o se refresque la página.
  const cargarCliente = useCallback(async () => {
    try {
      setCliente(await apiClientes.obtener(id));
    } catch (err) {
      setErrorCliente(err.message);
    } finally {
      setCargandoCliente(false);
    }
  }, [id]);

  useEffect(() => {
    setCargandoCliente(true);
    setErrorCliente(null);
    cargarCliente();
  }, [cargarCliente]);

  const cargarTransacciones = useCallback(async () => {
    try {
      const [datosVentas, datosAbonos] = await Promise.all([
        apiVentas.listar({ clienteId: id, pagina: paginaVentas, limite: POR_PAGINA }),
        apiAbonos.listar({ clienteId: id, pagina: paginaAbonos, limite: POR_PAGINA }),
      ]);
      setVentas(datosVentas);
      setAbonos(datosAbonos);
    } catch (error) {
      console.error("Error al cargar los movimientos:", error);
    }
  }, [id, paginaVentas, paginaAbonos]);

  const cargarSaldos = useCallback(async () => {
    try {
      setSaldos(await apiClientes.saldos(id));
    } catch (error) {
      console.error("Error al cargar los saldos:", error);
    }
  }, [id]);

  useEffect(() => {
    cargarTransacciones();
  }, [cargarTransacciones]);

  useEffect(() => {
    cargarSaldos();
  }, [cargarSaldos]);

  const recargar = () => Promise.all([cargarTransacciones(), cargarSaldos()]);

  const abrirEdicionVenta = async (venta) => {
    try {
      const detalles = await apiVentas.detalles(venta.id);
      setTransaccionEnEdicion({ ...venta, detalles, type: "venta" });
    } catch (error) {
      alert("No se pudieron cargar los detalles de la venta para edición.");
    }
  };

  const verDetalleAbono = async (abono) => {
    try {
      setAbonoVisto(await apiAbonos.detalle(abono.id));
    } catch (error) {
      alert(error.message);
    }
  };

  // El modal de edición reutiliza el formulario de siempre; el de alta es
  // el mismo de la pantalla de Movimientos, con el cliente ya puesto.
  const guardarEdicion = async (tipo, datos) => {
    const recurso = tipo === "venta" ? apiVentas : apiAbonos;
    try {
      await recurso.actualizar({ ...datos, id: transaccionEnEdicion.id });
      await recargar();
      setTransaccionEnEdicion(null);
      setAviso(tipo === "venta" ? "Venta actualizada." : "Abono actualizado.");
    } catch (error) {
      alert(error.message);
    }
  };

  // Se edita desde aquí para no tener que volver al listado solo para
  // cambiarle el teléfono o el límite de crédito.
  const guardarCliente = async (datos) => {
    await apiClientes.actualizar(datos);
    setEditandoCliente(false);
    await cargarCliente();
    setAviso("Datos del cliente actualizados.");
  };

  const confirmarEliminar = async () => {
    const { tipo, id: idRegistro } = confirmar;
    if (!tipo || !idRegistro) return;
    try {
      await (tipo === "venta" ? apiVentas : apiAbonos).eliminar(idRegistro);
      await recargar();
      setAviso(tipo === "venta" ? "Venta eliminada." : "Abono eliminado.");
    } catch (error) {
      alert(error.message);
    } finally {
      setConfirmar({ abierto: false, tipo: null, id: null });
    }
  };

  if (cargandoCliente) return <p>Cargando...</p>;

  if (errorCliente) {
    return (
      <div className="detail-view">
        <button onClick={() => navigate("/clientes")} className="back-btn">
          <BackIcon /> Volver a clientes
        </button>
        <p className="error-message">{errorCliente}</p>
      </div>
    );
  }

  const debe = Number(saldos.saldo_pendiente) > 0;

  // Lo que todavía puede llevarse a crédito antes de topar el límite.
  const limite = cliente.limite_credito === null || cliente.limite_credito === undefined
    ? null
    : Number(cliente.limite_credito);
  const disponible = limite === null ? null : limite - Number(saldos.saldo_pendiente);

  return (
    <div className="detail-view">
      {editandoCliente && (
        <ClientModal
          clientToEdit={cliente}
          onSave={guardarCliente}
          onClose={() => setEditandoCliente(false)}
        />
      )}

      {registrando && (
        <RegistrarMovimientoModal
          fecha={getTodayLocal()}
          clienteFijo={{ ...cliente, saldo_pendiente: saldos.saldo_pendiente }}
          onClose={() => setRegistrando(false)}
          onGuardado={() => {
            setRegistrando(false);
            setAviso("Movimiento registrado.");
            recargar();
          }}
        />
      )}

      {transaccionEnEdicion && (
        <AddTransactionModal
          type={transaccionEnEdicion.type}
          clienteId={cliente.id}
          onSave={guardarEdicion}
          onClose={() => setTransaccionEnEdicion(null)}
          transactionToEdit={transaccionEnEdicion}
        />
      )}

      {confirmar.abierto && (
        <ConfirmDeleteModal
          onConfirm={confirmarEliminar}
          onCancel={() => setConfirmar({ abierto: false, tipo: null, id: null })}
          message="¿Seguro que quieres eliminar este registro? No se puede deshacer."
        />
      )}

      {ventaVista && (
        <VentaDetailModal
          ventaId={ventaVista}
          cliente={cliente.nombre_completo}
          onClose={() => setVentaVista(null)}
        />
      )}

      {abonoVisto && (
        <AbonoDetailModal
          details={abonoVisto}
          cliente={cliente.nombre_completo}
          onClose={() => setAbonoVisto(null)}
        />
      )}

      <Link to="/clientes" className="back-btn">
        <BackIcon /> Volver a clientes
      </Link>

      <Aviso mensaje={aviso} onCerrar={() => setAviso(null)} />

      <header className="ficha-encabezado">
        <div>
          <h2>{cliente.nombre_completo}</h2>
          <div className="ficha-datos">
            <span>
              <PhoneIcon /> {cliente.telefono}
            </span>
            <span>
              <PinIcon /> {cliente.direccion || "Sin dirección"}
            </span>
          </div>
        </div>
        <div className="ficha-acciones">
          <button className="btn-secundario" onClick={() => setEditandoCliente(true)}>
            <EditIcon /> Editar
          </button>
          <BotonCompartir
            etiqueta="Compartir saldo"
            titulo="Estado de cuenta"
            archivo={`saldo-${cliente.nombre_completo.replace(/\s+/g, "-").toLowerCase()}.png`}
            generar={() => estadoDeCuenta({ cliente, saldos })}
          />
          <button className="add-btn" onClick={() => setRegistrando(true)}>
            <PlusIcon /> Registrar movimiento
          </button>
        </div>
      </header>

      <div className="totales-cliente">
        <TarjetaResumen
          clase="ventas"
          titulo="Total en compras"
          monto={saldos.total_todas_ventas}
          ocultable
        />
        <TarjetaResumen
          clase="abonos"
          titulo="Total abonado"
          monto={saldos.total_abonos}
          ocultable
        />
        <TarjetaResumen
          clase={debe ? "saldo-debe" : "saldo-al-dia"}
          titulo="Saldo pendiente"
          monto={saldos.saldo_pendiente}
          destacada
        />
      </div>

      {limite !== null && (
        <p className={`aviso-limite ${disponible <= 0 ? "sin-cupo" : ""}`}>
          Límite de crédito <strong>{formatCurrency(limite)}</strong>
          {disponible > 0 ? (
            <>
              {" · "}puede llevarse hasta <strong>{formatCurrency(disponible)}</strong> más a
              crédito
            </>
          ) : (
            <> · sin cupo disponible, solo de contado hasta que abone</>
          )}
        </p>
      )}

      <div className="historiales">
        <section className="historial">
          <h3>Compras</h3>
          {ventas.items && ventas.items.length > 0 ? (
            <ul className="lista-historial">
              {ventas.items.map((venta) => (
                <FilaHistorial
                  key={venta.id}
                  tipo={venta.tipo_pago === "contado" ? "contado" : "credito"}
                  titulo={formatDateTime(venta.fecha)}
                  subtitulo={[
                    venta.tipo_pago === "credito" ? "Crédito" : "Contado",
                    nombreFormaPago(venta.metodo_pago),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  monto={venta.monto_total}
                  onVer={() => setVentaVista(venta.id)}
                  onEditar={() => abrirEdicionVenta(venta)}
                  onEliminar={() =>
                    setConfirmar({ abierto: true, tipo: "venta", id: venta.id })
                  }
                />
              ))}
            </ul>
          ) : (
            <p className="historial-vacio">Todavía no hay compras registradas.</p>
          )}
          <PaginationControls
            currentPage={paginaVentas}
            totalPages={ventas.totalPages}
            onPageChange={setPaginaVentas}
          />
        </section>

        <section className="historial">
          <h3>Abonos</h3>
          {abonos.items && abonos.items.length > 0 ? (
            <ul className="lista-historial">
              {abonos.items.map((abono) => (
                <FilaHistorial
                  key={abono.id}
                  tipo="abono"
                  titulo={formatDateTime(abono.fecha)}
                  subtitulo={nombreFormaPago(abono.metodo_pago)}
                  monto={abono.monto}
                  onVer={() => verDetalleAbono(abono)}
                  onEditar={() =>
                    setTransaccionEnEdicion({ ...abono, type: "abono" })
                  }
                  onEliminar={() =>
                    setConfirmar({ abierto: true, tipo: "abono", id: abono.id })
                  }
                />
              ))}
            </ul>
          ) : (
            <p className="historial-vacio">Todavía no hay abonos registrados.</p>
          )}
          <PaginationControls
            currentPage={paginaAbonos}
            totalPages={abonos.totalPages}
            onPageChange={setPaginaAbonos}
          />
        </section>
      </div>
    </div>
  );
};

export default ClienteDetallePage;
